import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Clock, FileText, Download, ChevronDown, ChevronUp, Search, Filter,
  AlertCircle, CheckCircle, Loader2, User, Building, Mail, Phone,
  Calendar, MapPin, RefreshCw, MessageSquare, Upload, Send, X,
  AlertTriangle, Eye, Trash2
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WORKFLOW_STATES = {
  NEW_ENQUIRY: { label: "New", color: "bg-blue-500", priority: 1 },
  AWAITING_PAYMENT: { label: "Awaiting Payment", color: "bg-yellow-500", priority: 2 },
  PAYMENT_CONFIRMED: { label: "Payment Confirmed", color: "bg-green-500", priority: 3 },
  DOCUMENTS_INCOMPLETE: { label: "Documents Incomplete", color: "bg-orange-500", priority: 4 },
  READY_FOR_REVIEW: { label: "Ready for Review", color: "bg-purple-500", priority: 5 },
  REVIEW_IN_PROGRESS: { label: "Review in Progress", color: "bg-indigo-500", priority: 6 },
  CLARIFICATION_REQUIRED: { label: "Clarification Required", color: "bg-amber-500", priority: 7 },
  QUALITY_ASSURANCE: { label: "QA", color: "bg-teal-500", priority: 8 },
  REPORT_READY: { label: "Report Ready", color: "bg-emerald-500", priority: 9 },
  DELIVERED: { label: "Delivered", color: "bg-green-700", priority: 10 },
  CLOSED: { label: "Closed", color: "bg-gray-500", priority: 11 },
  DECLINED_CANCELLED: { label: "Declined/Cancelled", color: "bg-red-500", priority: 12 }
};

const PAYMENT_STATUSES = ["PENDING", "INVOICED", "PAID", "WAIVED", "REFUNDED"];

function calculateTimeRemaining(closingDate, closingTime) {
  if (!closingDate) return { text: "No deadline", urgent: false, overdue: false };
  
  try {
    const closeDateTime = new Date(`${closingDate}T${closingTime || "17:00"}`);
    const now = new Date();
    const diff = closeDateTime - now;
    
    if (diff < 0) {
      const hours = Math.abs(Math.floor(diff / (1000 * 60 * 60)));
      return { text: `${hours}h overdue`, urgent: true, overdue: true };
    }
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    
    if (days > 0) {
      return { text: `${days}d ${hours % 24}h`, urgent: days < 2, overdue: false };
    }
    return { text: `${hours}h`, urgent: hours < 48, overdue: false };
  } catch {
    return { text: "Invalid date", urgent: false, overdue: false };
  }
}

function formatDate(dateString) {
  if (!dateString) return "N/A";
  try {
    return new Date(dateString).toLocaleDateString("en-NZ", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  } catch {
    return dateString;
  }
}

export default function TenderReviewDashboard({ token }) {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedReview, setSelectedReview] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [sortBy, setSortBy] = useState("urgency"); // urgency, date, status
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [noteType, setNoteType] = useState("general");
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchReviews();
  }, [token]);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/tender-reviews`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReviews(response.data);
      setError(null);
    } catch (err) {
      setError("Failed to load tender reviews");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const updateWorkflowStatus = async (reviewId, newStatus, note = "") => {
    try {
      setUpdating(true);
      await axios.patch(
        `${API}/tender-reviews/${reviewId}/workflow`,
        { status: newStatus, note },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchReviews();
      if (selectedReview?.id === reviewId) {
        const updated = reviews.find(r => r.id === reviewId);
        if (updated) setSelectedReview({ ...updated, workflow_status: newStatus });
      }
    } catch (err) {
      alert("Failed to update status: " + (err.response?.data?.detail || err.message));
    } finally {
      setUpdating(false);
    }
  };

  const addNote = async () => {
    if (!selectedReview || !noteText.trim()) return;
    try {
      setUpdating(true);
      await axios.post(
        `${API}/tender-reviews/${selectedReview.id}/notes`,
        { note: noteText, note_type: noteType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNoteText("");
      setShowNoteModal(false);
      await fetchReviews();
      // Refresh selected review
      const updated = (await axios.get(`${API}/tender-reviews/${selectedReview.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })).data;
      setSelectedReview(updated);
    } catch (err) {
      alert("Failed to add note: " + (err.response?.data?.detail || err.message));
    } finally {
      setUpdating(false);
    }
  };

  const downloadFile = async (filePath) => {
    try {
      const response = await axios.get(`${API}/files/${filePath}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = filePath.split("/").pop();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      if (err.response?.status === 404) {
        alert("File not found. This file may have been uploaded before the current storage system was implemented, or it may have been deleted.");
      } else if (err.response?.status === 401) {
        alert("Session expired. Please log in again.");
      } else {
        alert("Download failed: " + (err.response?.data?.detail || err.message));
      }
    }
  };

  const resendNotification = async (reviewId, type) => {
    try {
      const result = await axios.post(
        `${API}/tender-reviews/${reviewId}/resend-notification?notification_type=${type}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(result.data.success ? "Notification sent!" : `Failed: ${result.data.error}`);
    } catch (err) {
      alert("Resend failed: " + (err.response?.data?.detail || err.message));
    }
  };

  // Filter and sort reviews
  const filteredReviews = reviews
    .filter(r => {
      if (statusFilter !== "ALL" && r.workflow_status !== statusFilter) return false;
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        return (
          r.reference_number?.toLowerCase().includes(search) ||
          r.tender_name?.toLowerCase().includes(search) ||
          r.company?.toLowerCase().includes(search) ||
          r.contact_name?.toLowerCase().includes(search)
        );
      }
      return true;
    })
    .sort((a, b) => {
      if (sortBy === "urgency") {
        const aTime = calculateTimeRemaining(a.closing_date, a.closing_time);
        const bTime = calculateTimeRemaining(b.closing_date, b.closing_time);
        if (aTime.overdue && !bTime.overdue) return -1;
        if (!aTime.overdue && bTime.overdue) return 1;
        if (aTime.urgent && !bTime.urgent) return -1;
        if (!aTime.urgent && bTime.urgent) return 1;
        return new Date(a.closing_date || "2099-12-31") - new Date(b.closing_date || "2099-12-31");
      }
      if (sortBy === "date") {
        return new Date(b.created_at) - new Date(a.created_at);
      }
      if (sortBy === "status") {
        return (WORKFLOW_STATES[a.workflow_status]?.priority || 99) - (WORKFLOW_STATES[b.workflow_status]?.priority || 99);
      }
      return 0;
    });

  const urgentCount = reviews.filter(r => {
    const time = calculateTimeRemaining(r.closing_date, r.closing_time);
    return (time.urgent || time.overdue) && !["DELIVERED", "CLOSED", "DECLINED_CANCELLED"].includes(r.workflow_status);
  }).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin mr-2" /> Loading tender reviews...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border-2 border-black p-4">
          <div className="text-sm font-mono text-zinc-500 uppercase">Total</div>
          <div className="text-3xl font-bold">{reviews.length}</div>
        </div>
        <div className={`border-2 border-black p-4 ${urgentCount > 0 ? "bg-red-50" : "bg-white"}`}>
          <div className="text-sm font-mono text-zinc-500 uppercase">Urgent</div>
          <div className={`text-3xl font-bold ${urgentCount > 0 ? "text-red-600" : ""}`}>
            {urgentCount}
          </div>
        </div>
        <div className="bg-white border-2 border-black p-4">
          <div className="text-sm font-mono text-zinc-500 uppercase">In Progress</div>
          <div className="text-3xl font-bold">
            {reviews.filter(r => ["REVIEW_IN_PROGRESS", "QUALITY_ASSURANCE"].includes(r.workflow_status)).length}
          </div>
        </div>
        <div className="bg-white border-2 border-black p-4">
          <div className="text-sm font-mono text-zinc-500 uppercase">Ready to Deliver</div>
          <div className="text-3xl font-bold">
            {reviews.filter(r => r.workflow_status === "REPORT_READY").length}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={20} />
          <input
            type="text"
            placeholder="Search by reference, project, company..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full h-12 pl-10 pr-4 border-2 border-black focus:shadow-brutal outline-none"
          />
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="h-12 px-4 border-2 border-black bg-white"
        >
          <option value="ALL">All Statuses</option>
          {Object.entries(WORKFLOW_STATES).map(([key, val]) => (
            <option key={key} value={key}>{val.label}</option>
          ))}
        </select>
        <select
          value={sortBy}
          onChange={e => setSortBy(e.target.value)}
          className="h-12 px-4 border-2 border-black bg-white"
        >
          <option value="urgency">Sort: Urgency</option>
          <option value="date">Sort: Newest First</option>
          <option value="status">Sort: Status</option>
        </select>
        <button
          onClick={fetchReviews}
          className="h-12 px-4 border-2 border-black bg-white hover:bg-zinc-100 flex items-center gap-2"
        >
          <RefreshCw size={18} /> Refresh
        </button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border-2 border-red-200 flex items-center gap-2 text-red-700">
          <AlertCircle size={20} /> {error}
        </div>
      )}

      {/* Reviews List */}
      <div className="space-y-4">
        {filteredReviews.length === 0 ? (
          <div className="text-center py-12 text-zinc-500">
            No tender reviews found
          </div>
        ) : (
          filteredReviews.map(review => {
            const timeRemaining = calculateTimeRemaining(review.closing_date, review.closing_time);
            const isSelected = selectedReview?.id === review.id;
            const statusInfo = WORKFLOW_STATES[review.workflow_status] || { label: review.workflow_status, color: "bg-gray-400" };
            const notificationFailed = review.notification_status?.admin === "failed" || review.notification_status?.customer === "failed";

            return (
              <div
                key={review.id}
                className={`border-2 border-black bg-white ${isSelected ? "shadow-brutal" : ""}`}
              >
                {/* Summary Row */}
                <div
                  className={`p-4 cursor-pointer hover:bg-zinc-50 ${timeRemaining.overdue ? "bg-red-50" : timeRemaining.urgent ? "bg-amber-50" : ""}`}
                  onClick={() => setSelectedReview(isSelected ? null : review)}
                >
                  <div className="flex flex-wrap items-center gap-4">
                    {/* Reference & Status */}
                    <div className="flex items-center gap-3">
                      <span className="font-mono font-bold">{review.reference_number}</span>
                      <span className={`px-2 py-1 text-xs text-white ${statusInfo.color}`}>
                        {statusInfo.label}
                      </span>
                      {notificationFailed && (
                        <span className="px-2 py-1 text-xs bg-orange-500 text-white flex items-center gap-1">
                          <AlertTriangle size={12} /> Email Failed
                        </span>
                      )}
                    </div>

                    {/* Project & Company */}
                    <div className="flex-1 min-w-0">
                      <div className="font-bold truncate">{review.tender_name}</div>
                      <div className="text-sm text-zinc-500">{review.company} • {review.contact_name}</div>
                    </div>

                    {/* Region */}
                    <div className="text-sm font-mono">
                      {review.region?.toUpperCase()}
                    </div>

                    {/* Time Remaining */}
                    <div className={`text-sm font-mono ${timeRemaining.overdue ? "text-red-600 font-bold" : timeRemaining.urgent ? "text-amber-600" : "text-zinc-500"}`}>
                      {timeRemaining.overdue && <AlertTriangle className="inline mr-1" size={14} />}
                      {timeRemaining.text}
                    </div>

                    {/* Docs Count */}
                    <div className="flex items-center gap-1 text-sm text-zinc-500">
                      <FileText size={14} />
                      {review.file_paths?.length || 0}
                    </div>

                    {/* Expand/Collapse */}
                    {isSelected ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </div>
                </div>

                {/* Expanded Details */}
                {isSelected && (
                  <div className="border-t-2 border-black p-4 space-y-6">
                    {/* Contact Info */}
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <div className="text-xs font-mono text-zinc-500 uppercase mb-1">Contact</div>
                        <div className="flex items-center gap-2">
                          <User size={16} className="text-zinc-400" />
                          {review.contact_name}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <Mail size={16} className="text-zinc-400" />
                          <a href={`mailto:${review.email}`} className="text-blue-600 hover:underline">
                            {review.email}
                          </a>
                        </div>
                        {review.phone && (
                          <div className="flex items-center gap-2 mt-1">
                            <Phone size={16} className="text-zinc-400" />
                            {review.phone}
                          </div>
                        )}
                      </div>

                      <div>
                        <div className="text-xs font-mono text-zinc-500 uppercase mb-1">Company</div>
                        <div className="flex items-center gap-2">
                          <Building size={16} className="text-zinc-400" />
                          {review.company}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <MapPin size={16} className="text-zinc-400" />
                          {review.region?.toUpperCase()} • {review.contractor_type}
                        </div>
                      </div>

                      <div>
                        <div className="text-xs font-mono text-zinc-500 uppercase mb-1">Tender Close</div>
                        <div className="flex items-center gap-2">
                          <Calendar size={16} className="text-zinc-400" />
                          {review.closing_date} {review.closing_time}
                        </div>
                        <div className={`mt-1 font-mono ${timeRemaining.overdue ? "text-red-600 font-bold" : timeRemaining.urgent ? "text-amber-600" : ""}`}>
                          {timeRemaining.text} remaining
                        </div>
                      </div>
                    </div>

                    {/* Tender Details */}
                    <div className="grid md:grid-cols-2 gap-4 p-4 bg-zinc-50">
                      <div>
                        <div className="text-xs font-mono text-zinc-500 uppercase mb-1">Tender Status</div>
                        <div>{review.tender_status}</div>
                      </div>
                      <div>
                        <div className="text-xs font-mono text-zinc-500 uppercase mb-1">Approximate Value</div>
                        <div>{review.approximate_value || "Not specified"}</div>
                      </div>
                      {review.primary_concern && (
                        <div className="md:col-span-2">
                          <div className="text-xs font-mono text-zinc-500 uppercase mb-1">Primary Concern</div>
                          <div>{review.primary_concern}</div>
                        </div>
                      )}
                    </div>

                    {/* Workflow Controls */}
                    <div className="flex flex-wrap items-center gap-4 p-4 border-2 border-zinc-200">
                      <div>
                        <div className="text-xs font-mono text-zinc-500 uppercase mb-1">Workflow Status</div>
                        <select
                          value={review.workflow_status}
                          onChange={e => updateWorkflowStatus(review.id, e.target.value)}
                          disabled={updating}
                          className="h-10 px-3 border-2 border-black bg-white"
                        >
                          {Object.entries(WORKFLOW_STATES).map(([key, val]) => (
                            <option key={key} value={key}>{val.label}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <div className="text-xs font-mono text-zinc-500 uppercase mb-1">Payment</div>
                        <select
                          value={review.payment_status || "PENDING"}
                          onChange={async e => {
                            await axios.patch(
                              `${API}/tender-reviews/${review.id}/workflow`,
                              { status: review.workflow_status, payment_status: e.target.value },
                              { headers: { Authorization: `Bearer ${token}` } }
                            );
                            fetchReviews();
                          }}
                          className="h-10 px-3 border-2 border-black bg-white"
                        >
                          {PAYMENT_STATUSES.map(s => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <div className="text-xs font-mono text-zinc-500 uppercase mb-1">Assigned To</div>
                        <input
                          type="text"
                          value={review.assigned_reviewer || ""}
                          placeholder="Reviewer name"
                          onChange={async e => {
                            await axios.patch(
                              `${API}/tender-reviews/${review.id}/workflow`,
                              { status: review.workflow_status, assigned_reviewer: e.target.value },
                              { headers: { Authorization: `Bearer ${token}` } }
                            );
                            fetchReviews();
                          }}
                          className="h-10 px-3 border-2 border-black"
                        />
                      </div>

                      <button
                        onClick={() => setShowNoteModal(true)}
                        className="h-10 px-4 border-2 border-black bg-white hover:bg-zinc-100 flex items-center gap-2"
                      >
                        <MessageSquare size={16} /> Add Note
                      </button>
                    </div>

                    {/* Documents */}
                    {review.file_paths?.length > 0 && (
                      <div>
                        <div className="text-xs font-mono text-zinc-500 uppercase mb-2">Documents ({review.file_paths.length})</div>
                        <div className="grid md:grid-cols-2 gap-2">
                          {review.file_paths.map((path, idx) => (
                            <button
                              key={idx}
                              onClick={() => downloadFile(path)}
                              className="flex items-center gap-2 p-3 border border-zinc-300 hover:bg-zinc-100 text-left"
                            >
                              <FileText size={18} className="text-zinc-500" />
                              <span className="flex-1 truncate font-mono text-sm">{path.split("/").pop()}</span>
                              <Download size={16} className="text-zinc-400" />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Report Upload */}
                    {review.report_path ? (
                      <div className="p-4 bg-green-50 border-2 border-green-200">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-xs font-mono text-green-600 uppercase mb-1">Report Uploaded</div>
                            <div className="font-mono">{review.report_filename}</div>
                          </div>
                          <button
                            onClick={() => downloadFile(review.report_path)}
                            className="h-10 px-4 border-2 border-green-600 text-green-600 hover:bg-green-100 flex items-center gap-2"
                          >
                            <Download size={16} /> Download
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="p-4 bg-zinc-50 border-2 border-dashed border-zinc-300">
                        <div className="text-center text-zinc-500">
                          <Upload size={24} className="mx-auto mb-2" />
                          <p className="text-sm">Report not yet uploaded</p>
                        </div>
                      </div>
                    )}

                    {/* Internal Notes */}
                    {review.internal_notes?.length > 0 && (
                      <div>
                        <div className="text-xs font-mono text-zinc-500 uppercase mb-2">Internal Notes</div>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {review.internal_notes.map((note, idx) => (
                            <div key={idx} className="p-3 bg-yellow-50 border border-yellow-200">
                              <div className="flex justify-between text-xs text-zinc-500 mb-1">
                                <span className="font-mono uppercase">{note.note_type}</span>
                                <span>{formatDate(note.created_at)} by {note.created_by}</span>
                              </div>
                              <div>{note.note}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Notification Resend */}
                    {notificationFailed && (
                      <div className="p-4 bg-orange-50 border-2 border-orange-200">
                        <div className="flex items-center justify-between">
                          <div>
                            <AlertTriangle className="inline text-orange-500 mr-2" size={18} />
                            <span className="text-orange-700">Email notification failed</span>
                          </div>
                          <div className="flex gap-2">
                            {review.notification_status?.admin === "failed" && (
                              <button
                                onClick={() => resendNotification(review.id, "admin")}
                                className="px-3 py-1 border border-orange-500 text-orange-600 text-sm hover:bg-orange-100"
                              >
                                Resend Admin
                              </button>
                            )}
                            {review.notification_status?.customer === "failed" && (
                              <button
                                onClick={() => resendNotification(review.id, "customer")}
                                className="px-3 py-1 border border-orange-500 text-orange-600 text-sm hover:bg-orange-100"
                              >
                                Resend Customer
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Status History */}
                    {review.status_history?.length > 0 && (
                      <div>
                        <div className="text-xs font-mono text-zinc-500 uppercase mb-2">Status History</div>
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                          {review.status_history.slice().reverse().map((entry, idx) => (
                            <div key={idx} className="flex items-center gap-2 text-sm text-zinc-600">
                              <span className="font-mono text-xs text-zinc-400">{formatDate(entry.timestamp)}</span>
                              <span className={`px-2 py-0.5 text-xs text-white ${WORKFLOW_STATES[entry.status]?.color || "bg-gray-400"}`}>
                                {WORKFLOW_STATES[entry.status]?.label || entry.status}
                              </span>
                              {entry.note && <span className="text-zinc-500">- {entry.note}</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Metadata */}
                    <div className="text-xs text-zinc-400 font-mono">
                      Created: {formatDate(review.created_at)} | Updated: {formatDate(review.updated_at)} | ID: {review.id}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Add Note Modal */}
      {showNoteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white border-4 border-black p-6 max-w-lg w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-heading font-bold text-xl uppercase">Add Internal Note</h3>
              <button onClick={() => setShowNoteModal(false)}>
                <X size={24} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-mono uppercase mb-1">Note Type</label>
                <select
                  value={noteType}
                  onChange={e => setNoteType(e.target.value)}
                  className="w-full h-10 px-3 border-2 border-black"
                >
                  <option value="general">General</option>
                  <option value="clarification">Clarification</option>
                  <option value="payment">Payment</option>
                  <option value="delivery">Delivery</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-mono uppercase mb-1">Note</label>
                <textarea
                  value={noteText}
                  onChange={e => setNoteText(e.target.value)}
                  rows={4}
                  className="w-full p-3 border-2 border-black"
                  placeholder="Enter internal note..."
                />
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowNoteModal(false)}
                  className="px-4 py-2 border-2 border-black"
                >
                  Cancel
                </button>
                <button
                  onClick={addNote}
                  disabled={!noteText.trim() || updating}
                  className="px-4 py-2 bg-black text-white border-2 border-black disabled:opacity-50"
                >
                  {updating ? <Loader2 className="animate-spin" size={18} /> : "Save Note"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
